import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subscription, combineLatest, forkJoin, of} from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { MessageService } from 'primeng/api';

import { DashboardApi } from '../../../api/dashboard/dashboard.api';
import { ReportBlockTypeApiEnum } from '../../../api/reports/enum/report-block-type-api.enum';

import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { UserCurrency, currencySymbolMap } from '../../../Core/Users/enums/user-currency';

import { ReportBlockTypeEnum } from '../../Reports/enums/report-block-type.enum';
import { ChartMetricsEnum } from '../../Reports/enums/chart-metrics.enum';
import { ChartDisplayElementModel } from '../../Reports/models/chart-display-element.model';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { BlockModel } from '../../PageBuilder/models/block.model';
import { SeriesTypeEnum } from '../../Reports/enums/series-type.enum';

import { DashboardModel } from '../models/dashboard.model';
import { reportChartOptions, dashboardChartOptions } from '../models/chart-setup-option.model';
import { ChartContentFilterModel } from '../models/chart-content-filter.model';
import { MediaSubscriptionService } from '../../../Monitor/Core/services/media-subscription.service';
import { ThemeService } from '../../../Social/Intelligence/services/theme.service';
import { SavedMentionSearchesApi } from '../../../api/mentions/saved-mention-searches.api';
import { ReportSeriesModel } from '../../Reports/models/report-series.model';
import { SavedSearchType } from '../../MonitorShared/enum/saved-search-type.enum';
import { Mention } from '../../Mentions/models/mention';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { MentionSearchModel } from '../../../Monitor/Shared/models/mention-search.model';
import { MentionSearchQueryModel } from '../../../Monitor/Shared/models/mention-search-query.model';
import { JournodexRegionsService } from '../../Journodex/services/journodex-regions.service';
import { AgentService } from '../../../Social/Intelligence/services/agent.service';

@Injectable()
export class DashboardService implements OnDestroy {

    subscriptions: Subscription[] = [];

    oldDashboard: DashboardModel = new DashboardModel();
    currentDashboard: DashboardModel = null;
    private currentDashboard$: BehaviorSubject<DashboardModel> = new BehaviorSubject<DashboardModel>(this.currentDashboard);

    private saveDashboardToApi$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    hasTargeting: boolean = false;
    hasMonitoring: boolean = false;
    hasSocial: boolean = false;

    currentDashboardRequest: Observable<any>;

    listOfModifiedCharts: ChartDisplayElementModel[] = [];

    currency: UserCurrency;

    constructor(
        private dashboardApi: DashboardApi,
        private savedSearchesApi: SavedMentionSearchesApi,
        private mentionsApi: MentionsApi,
        private journodexService: JournodexRegionsService,
        private messageService: MessageService,
        private currentUserStateService: CurrentUserStateService,
        private localizationService: LocalizationService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public themeService: ThemeService,
        private agentService: AgentService
    ) {
        this.setupSubscriptions();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    setupSubscriptions() {
        // setup the save dashboard to api behind a debounce
        // so if multiple request happen at once, we only want the last request to go through
        // we need this because we setup the save request to happen on gridsters itemChangeCallback function in DashboardPageBuilderService
        // which gets fired for EVERY block that was moved, so if you drag a block, and another block moves, the request would happen twice.
        this.subscriptions.push(
            this.saveDashboardToApi$
                .pipe(
                    filter(x => !!x), // Ignore initial subscribe on null
                    debounceTime(250), // quarter a second
                    switchMap((data) => {
                        return forkJoin([this.dashboardApi.saveDashboard(data?.dashboardApimodel), of(data?.lastChartTouched)]);
                    })
                )
                .subscribe({
                    next: ([dashboardId, lastChartTouched]) => {
                        this.showAlertMessage(lastChartTouched);

                        if (dashboardId) {
                            this.currentDashboard.id = (dashboardId as number);
                        }
                        this.oldDashboard = this.currentDashboard.clone();
                        this.listOfModifiedCharts = [];
                    },
                    error: (error) => {
                        console.log('error while saving dashboard', error);

                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Dashboard.update.failed.heading',
                            detail: 'Dashboard.update.failed.message',
                        });
                        this.listOfModifiedCharts = [];
                    }
                }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                if (prefs && prefs.currency !== this.currency) {
                    this.currency = prefs.currency;
                }
            })
        );
    }

    showAlertMessage(lastChartTouched) {
        let message = 'Dashboard.add.success.message.chart';

        if (lastChartTouched?.title) {
            if (this.oldDashboard.blocks.length === this.currentDashboard.blocks.length) {
                if (this.listOfModifiedCharts.length > 1) {
                    message = 'Dashboard.update.success.message.multiple';
                } else {
                    message = 'Dashboard.update.success.message.chart';
                }
            } else if (this.oldDashboard.blocks.length > this.currentDashboard.blocks.length) {
                message = 'Dashboard.delete.success.message.chart';
            }
            message = this.localizationService.transform(message, {
                chartTitle: lastChartTouched.title,
            });
        } else {
            message = 'Dashboard.add.success.message';
            if (this.oldDashboard.blocks.length === this.currentDashboard.blocks.length) {
                message = 'Dashboard.update.success.message';
            } else if (this.oldDashboard.blocks.length > this.currentDashboard.blocks.length) {
                message = 'Dashboard.delete.success.message';
            }
        }

        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: 'Dashboard.update.success.heading',
            detail: message,
        });
    }

    public loadDashboard(): void {
        // not allow multiple get requests at the same time
        if (this.currentDashboardRequest) {
            return;
        }

        // we need to get the user/account permissions before returning the dashboard so we can filter out any charts that do not belong
        // very small use case, ex: if account HAS advTargeting, add charts.. then removes access to advTargeting..
        this.currentUserStateService.getPermissions().pipe(
            filter(x => !!x),
            debounceTime(250), // sometimes we set the permissions 3 times very fast
            switchMap(permissions => {
                this.hasTargeting = permissions.includes(userPermissionsEnum.advancedTargeting);
                this.hasMonitoring = permissions.includes(userPermissionsEnum.advancedMonitoring);
                this.hasSocial = permissions.includes(userPermissionsEnum.socialIntelligence);

                this.currentDashboardRequest = forkJoin(this.hasSocial ? {
                    apiDashboard: this.dashboardApi.getDashboard(),
                    themes: this.themeService.all$.pipe(filter(x => !!x), take(1)), // @HACK, see NEP-19081
                    mediaSubscriptions: this.agentService.all$.pipe(filter(x => !!x), take(1)) // @HACK, see NEP-19081
                } : {
                    apiDashboard: this.dashboardApi.getDashboard()
                });

                // we cannot do the permissions & getDashboard dashboard in parallel,
                // because if getDashboard fails we then do not set the permissions
                return this.currentDashboardRequest;
            }),
            map(({ apiDashboard }) => {
                const dashboard = new DashboardModel(apiDashboard);

                const targetingChartTypes = dashboardChartOptions
                    .filter(chart => chart.permissions.includes(userPermissionsEnum.advancedTargeting))
                    .map(chart => chart.blockType.key);

                const monitoringChartTypes = dashboardChartOptions
                    .filter(chart => chart.permissions.includes(userPermissionsEnum.advancedMonitoring))
                    .map(chart => chart.blockType.key);

                const socialCharts = dashboardChartOptions
                    .filter(chart => chart.permissions.includes(userPermissionsEnum.socialIntelligence))
                    .map(chart => chart.blockType.key);

                const reportCharts = reportChartOptions
                    .filter(chart => chart.permissions.includes(userPermissionsEnum.advancedMonitoring))
                    .map(chart => chart.blockType.key);

                // filter out the dashboard blocks that the user does not have access too
                dashboard.blocks = dashboard.blocks ? dashboard.blocks.filter(block => {
                    if (!this.hasTargeting && targetingChartTypes.includes(block.type.key)) {
                        return false;
                    }
                    if ((!this.hasMonitoring && monitoringChartTypes.includes(block.type.key))
                        && (!this.hasSocial && socialCharts.includes(block.type.key))
                    ) {
                        return false;
                    }
                    if (!this.hasMonitoring && !this.hasSocial && reportCharts.includes(block.type.key)) {
                        return false;
                    }
                    return true;
                }) : [];

                return dashboard;
            }),
            switchMap(dashboard => this.loadDashboardSavedSearches(dashboard)),
            tap(dashboard => {
                dashboard.blocks.forEach(block => this.updateDisplayElementFromSeries(block));
                this.setCurrentDashboard(dashboard);
                this.oldDashboard = this.currentDashboard.clone();
                this.currentDashboardRequest = null;
            }),
            catchError(error => {
                if ((error?.status === 404) && (error?.error === 'Dashboard does not exist')) {
                    this.setupDefaultDashboard();
                } else if (this.currentDashboard?.id) {
                    this.clearDashboard();
                    this.getCurrentDashboard();
                } else {
                    // any error retrieving the dashboard, we create a new blank dashboard
                    this.setCurrentDashboard(new DashboardModel());
                }
                this.currentDashboardRequest = null;
                return EMPTY;
            })
        ).subscribe();
    }

    /**
     * Loads the saved filter searches for the dashboard charts
     */
    private loadDashboardSavedSearches(dashboard: DashboardModel): Observable<DashboardModel> {
        const series = dashboard.getSeries();

        this.loadMentions(dashboard);

        let searchIds = series.filter(item => !!item.mentions.searchCriteriaId).map(item => item.mentions.searchCriteriaId);
        searchIds = [...new Set(searchIds)];

        if (searchIds.length < 1) {
            return of(dashboard);
        }

        return this.savedSearchesApi.getSavedSearchBulk(searchIds).pipe(
            mergeMap(search => {
                const loadRegions: Observable<void>[] = [];
                series.forEach(item => {
                    if (item.mentions.searchCriteriaId) {
                        item.mentions.filters = new MentionSearchModel(search[item.mentions.searchCriteriaId]);
                        loadRegions.push(this.loadSavedSearchRegions(item.mentions.filters));
                    }
                });

                if (loadRegions.length === 0){
                    return of(dashboard);
                }

                return forkJoin(loadRegions).pipe(
                    map(topics => dashboard)
                );
            })
        );
    }

    /**
     * for topCuratedMentions, it will do a batch get of the saved mentions, and update with the latest mentions
     */
    private loadMentions(dashboard: DashboardModel): void {
        const blocks = dashboard?.blocks;

        if (blocks?.length < 1) {
            return;
        }

        blocks.forEach(block => {
            if ((block.displayElement as ChartDisplayElementModel)?.styles?.topCuratedMentions?.length) {
                const chartDisplayElement = block.displayElement as ChartDisplayElementModel;
                const mentionIds = chartDisplayElement?.styles?.topCuratedMentions.map(mention => mention.id);
                this.mentionsApi.getMentionsBatched(mentionIds).subscribe(mentions => {
                    for (const index in chartDisplayElement.styles.topCuratedMentions) {
                        if (mentions[chartDisplayElement.styles.topCuratedMentions[index].id]) {
                            chartDisplayElement.styles.topCuratedMentions[index] = mentions[chartDisplayElement.styles.topCuratedMentions[index].id];
                        }
                    }

                    chartDisplayElement.topCuratedMentions = JSON.parse(
                        JSON.stringify(chartDisplayElement?.styles?.topCuratedMentions)
                    )?.map(x => new Mention(x)) || [];

                    chartDisplayElement.series[0].mentionProvider.next(chartDisplayElement.topCuratedMentions);
                });
            }
        });
    }

    /**
     * Loads the regions for a search object
     */
    private loadSavedSearchRegions(search: MentionSearchModel): Observable<void> {
        const query = search.searchCriteria.query;
        const includeRegions = query.rawIncludedRegions;
        if (!MentionSearchQueryModel.isRawRegionEmpty(includeRegions)) {
            return this.journodexService.mapToComplexRegionInfo(includeRegions).pipe(map(
                regions => {
                    query.includedRegions = regions;
                    query.excludedRegions = [];
                    query.rawIncludedRegions = null;
                    query.rawExcludedRegions = null;
                }
            ));
        }
        return of(null);
    }

    setupDefaultDashboard() {
        const data = [];

        if (this.hasTargeting) {
            data.push({
                type: ReportBlockTypeApiEnum.RECENT_TARGETING_ACTIVITY,
                title: this.localizationService.transform(
                    `Dashboard.chart.title.${ReportBlockTypeEnum.RECENT_TARGETING_ACTIVITY}`
                ),
                x: 0,
                y: 0,
                cols: 2,
                rows: 2,
                data: {
                    numOfItems: 5,
                    contentFilters: new ChartContentFilterModel()
                },
                series: []
            });

            data.push({
                type: ReportBlockTypeApiEnum.RECENT_DISTRIBUTION_ACTIVITY,
                title: this.localizationService.transform(
                    `Dashboard.chart.title.${ReportBlockTypeEnum.RECENT_DISTRIBUTION_ACTIVITY}`
                ),
                x: 0,
                y: 0,
                cols: 2,
                rows: 2,
                data: {
                    numOfItems: 5,
                    contentFilters: new ChartContentFilterModel()
                },
                series: []
            });

            data.push({
                type: ReportBlockTypeApiEnum.RECENT_REPORTS_DISTRIBUTIONS,
                title: this.localizationService.transform(
                    `Dashboard.chart.title.${ReportBlockTypeEnum.RECENT_REPORTS_DISTRIBUTIONS}`
                ),
                x: 0,
                y: 0,
                cols: 4,
                rows: 2,
                data: {
                    numOfItems: 5,
                    contentFilters: new ChartContentFilterModel()
                },
                series: []
            });
        }

        if (this.hasMonitoring) {
            data.push({
                type: ReportBlockTypeApiEnum.RECENT_TOPICS,
                title: this.localizationService.transform(
                    `Dashboard.chart.title.${ReportBlockTypeEnum.RECENT_TOPICS}`
                ),
                x: 0,
                y: 0,
                cols: 4,
                rows: 2,
                data: {
                    seriesCount: 4,
                    type: SeriesTypeEnum.topic
                },
                series: []
            });
        }

        const defaultDashboardApi = {
            data: JSON.stringify(data)
        };
        this.setCurrentDashboard(new DashboardModel(defaultDashboardApi));
    }

    saveCurrentDashboard(lastChartTouched?: ChartDisplayElementModel) {
        if (this.currentDashboard) {
            this.saveDashboard(this.currentDashboard, lastChartTouched);
        }
    }

    saveDashboard(dashboard: DashboardModel, lastChartTouched?: ChartDisplayElementModel) {
        this.saveDashboardSavedSearches(dashboard).subscribe(() => {
            const dashboardApimodel = dashboard.clone().toApiModel();
            this.listOfModifiedCharts.push(lastChartTouched);
            this.saveDashboardToApi$.next({dashboardApimodel, lastChartTouched});
        });
    }

    private saveDashboardSavedSearches(dashboard: DashboardModel): Observable<any[]> {
        const seriesArray: ReportSeriesModel[] = dashboard.getSeries();
        const requests: Observable<any>[] = [];

        for (const series of seriesArray) {
            if (series.mentions.searchCriteriaId) {
                if (series.mentions.filters) {
                    // already exists, update
                    series.mentions.filters.id = series.mentions.searchCriteriaId;
                    series.mentions.filters.searchCriteria.query.dateQuery = null;
                    if (!series.mentions.filters.name) {
                        // Name can't be blank, must be unique
                        series.mentions.filters.name = `${+new Date()}${Math.random()}`;
                    }
                    requests.push(
                        this.savedSearchesApi.update(series.mentions.filters.toApiModel())
                    );
                } else {
                    // filters remove, so delete
                    requests.push(
                        this.savedSearchesApi.deleteById(series.mentions.searchCriteriaId).pipe(
                            tap(() => {
                                delete series.mentions.searchCriteriaId;
                            }))
                    );
                }
            } else if (series.mentions.filters) {
                // create new
                if (!series.mentions.filters.name) {
                    // Name can't be blank, must be unique
                    series.mentions.filters.name = `${+new Date()}${Math.random()}`;
                }
                series.mentions.filters.type = SavedSearchType.REPORTS;
                series.mentions.filters.searchCriteria.query.dateQuery = null;

                requests.push(
                    this.savedSearchesApi.create(series.mentions.filters.toApiModel()).pipe(
                        tap(id => {
                            series.mentions.searchCriteriaId = id;
                        }))
                );
            }
        }

        if (requests.length < 1) {
            return of([]);
        }
        return forkJoin(requests);
    }

    getCurrentDashboard(): Observable<DashboardModel> {
        if (!this.currentDashboard) {
            this.loadDashboard();
        }

        return this.currentDashboard$;
    }

    clearDashboard(): void {
        this.setCurrentDashboard(null);
    }

    private setCurrentDashboard(dashboard: DashboardModel): void {
        this.currentDashboard = dashboard;
        this.currentDashboard$.next(this.currentDashboard);
    }

    /**
     * Shortcut to just update the blocks of the current dashboard
     */
    public setCurrentDashboardBlocks(blocks: BlockModel[], lastChartTouched?: ChartDisplayElementModel): void {
        this.currentDashboard.blocks = blocks;

        this.saveCurrentDashboard(lastChartTouched);

        this.currentDashboard$.next(this.currentDashboard);
        this.updateDashboardBlocks();
    }

    updateDashboardBlocks(): void {
        this.currentDashboard.blocks.forEach(block => {
            this.updateDisplayElementFromSeries(block);
        });
    }

    /**
     * currently has three functionalities
     * 1) set displayElement colorMap depending on series
     * 2) sets series metric depending on displayElement
     * 3) sets the currency of the block to its series
     */
    private updateDisplayElementFromSeries(block: BlockModel): void {
        const displayElement = <ChartDisplayElementModel>block.displayElement;

        const series = displayElement.series;
        series?.forEach((series_, i) => {
            series_.metric = displayElement.metric;
            series_.metrics = displayElement.metrics;
            series_.hasPreviousPeriod = displayElement.styles.chartDisplaySettings.previousPeriod;

            if (!!displayElement.metrics){
                if (displayElement.metrics.indexOf(ChartMetricsEnum.ave) !== -1){

                    if (displayElement.styles.currency !== this.currency) {
                        displayElement.styles.currency = this.currency;
                    }
                    series_.styles.currency = displayElement.styles.currency;
                    series_.styles.currencySymbol = currencySymbolMap.get(displayElement.styles.currency);
                } else {
                    delete series_.styles.currency;
                    delete series_.styles.currencySymbol;
                }
            } else {
                if (displayElement.metric === ChartMetricsEnum.ave){
                    if (displayElement.styles.currency !== this.currency) {
                        displayElement.styles.currency = this.currency;
                    }
                    series_.styles.currency = displayElement.styles.currency;
                    series_.styles.currencySymbol = currencySymbolMap.get(displayElement.styles.currency);
                } else {
                    delete series_.styles.currency;
                    delete series_.styles.currencySymbol;
                }
            }

            if (series_.color) {
                switch (block.type.key) {
                    case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
                    case ReportBlockTypeEnum.TOP_10_OUTLETS:
                    case ReportBlockTypeEnum.TOP_HASHTAGS:
                        // backwards compat layer - the color for the series used to be stored on the key 'barColor',
                        // but now is stored properly on the series. this conditional can be removed after some time
                        // has passed, as just interacting with a report will cause this code to run, which migrates
                        // the color property.
                        if (displayElement.styles.colorMap['barColor']) {
                            series_.color = displayElement.styles.colorMap['barColor'];
                            delete displayElement.styles.colorMap['barColor'];
                        }
                        displayElement.styles.colorMap[`seriesColor${i}`] = series_.color;
                        break;

                    case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                    case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                    case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                    case ReportBlockTypeEnum.SHARE_OF_VOICE:
                    case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                    case ReportBlockTypeEnum.REFERRAL_TREND:
                    case ReportBlockTypeEnum.RECENT_TOPICS:
                        displayElement.styles.colorMap[`seriesColor${i}`] = series_.color;
                        break;

                    default:
                        break;
                }
            }
        });
    }
}
